/**
 * The CC0 packs that ship with the world client (not listed by the approved-asset API). Each
 * group of files has its own LICENSES.md under `public/world/`. CC0 asks for no attribution; the
 * authors are credited anyway.
 */
export interface BuiltinCredit {
  id: string;
  name: string;
  author: string;
  license: "CC0-1.0" | "MIT";
  source: string;
  /** What it is used for. */
  use: string;
}

export const INTERIOR_CREDITS: readonly BuiltinCredit[] = [
  {
    id: "kenney-furniture",
    name: "Furniture Kit",
    author: "Kenney",
    license: "CC0-1.0",
    source: "https://opengameart.org/content/furniture-kit",
    use: "Office chairs, monitors, keyboards, coffee tables, fridge (interiors)",
  },
  {
    id: "polyhaven-furniture",
    name: "Poly Haven furniture and plants",
    author:
      "Vibrant Nordic, Ulan Cabanilla, Arrangemonk, Benny Weimer, Rico Cilliers, Kirill Sannikov, James Ray Cock, ParzivalCG, Joe Seabuhr",
    license: "CC0-1.0",
    source: "https://polyhaven.com/models",
    use: "Desks, armchairs, sofa, shelves, plants, meeting table and chairs, lamps (interiors)",
  },
  {
    id: "ambientcg-interior",
    name: "Office surfaces: carpet, wood floor, plaster, concrete, acoustic ceiling",
    author: "ambientCG",
    license: "CC0-1.0",
    source: "https://ambientcg.com",
    use: "Floors, walls, ceilings and stairs (interiors)",
  },
];

/** The Poly Haven pack of the themed Uzmoon floors: models, PBR surface sets and the city HDRI (CC0). */
export const PLAZA_INTERIOR_CREDITS: readonly BuiltinCredit[] = [
  {
    id: "polyhaven-plaza-models",
    name: "Poly Haven furniture, art and plants (29 models)",
    author:
      "Dairon Sanchez, John Malcolm, Rico Cilliers, Tina, Rob Tuytel, James Ray Cock, Ulan Cabanilla, Yann Kervran, Kuutti Siitonen, Amin, Patrik Pangerl, Caspian Fortune, PeterM, Fran Calvente, Jurita Burger, Serhii Khromov, Jenelle van Heerden",
    license: "CC0-1.0",
    source: "https://polyhaven.com/models",
    use: "Sofas, lounge chairs, cabinets, shelves, art, plants and decor on the themed floors",
  },
  {
    id: "polyhaven-plaza-surfaces",
    name: "Poly Haven surfaces: granite, marble, terrazzo, parquet, oak, walnut, velvet, concrete, plaster",
    author:
      "Charlotte Baglioni, Rob Tuytel, Jenelle van Heerden, Sergej Majboroda, Amal Kumar, Dimitrios Savva, colormass, Rico Cilliers",
    license: "CC0-1.0",
    source: "https://polyhaven.com/textures",
    use: "Floors, walls and feature walls of the themed floors (15 sets)",
  },
  {
    id: "polyhaven-neuer-zollhof",
    name: "Neuer Zollhof (HDRI)",
    author: "Greg Zaal",
    license: "CC0-1.0",
    source: "https://polyhaven.com/a/neuer_zollhof",
    use: "Indoor reflections (city image at low intensity)",
  },
];

/** The exterior and the living city: trees, vehicles, street furniture and pedestrians (Quaternius). */
const quaternius = (id: string, name: string, slug: string, use: string): BuiltinCredit => ({
  id: `quaternius-${id}`,
  name,
  author: "Quaternius",
  license: "CC0-1.0",
  source: `https://quaternius.com/packs/${slug}.html`,
  use,
});

export const CITY_CREDITS: readonly BuiltinCredit[] = [
  quaternius("nature", "Ultimate Nature Pack", "ultimatenature", "Trees and bushes (exterior)"),
  quaternius("cars", "Cars", "cars", "Cars driving on the streets"),
  quaternius(
    "transport",
    "Public Transport",
    "publictransport",
    "Buses, bicycles and traffic cones",
  ),
  quaternius(
    "streets",
    "Modular Streets",
    "modularstreets",
    "Street lamps, traffic lights and road signs",
  ),
  quaternius(
    "props",
    "Ultimate Home Interior",
    "ultimatehomeinterior",
    "Trash cans on the sidewalks",
  ),
  quaternius(
    "characters",
    "Ultimate Modular Characters",
    "ultimatemodularcharacters",
    "Animated pedestrians (Walk and Idle clips)",
  ),
];

/** The realistic human characters: the user's avatar, the assistant, the agents in the floors. */
export const CHARACTER_CREDITS: readonly BuiltinCredit[] = [
  quaternius(
    "universal-base-characters",
    "Universal Base Characters",
    "universalbasecharacters",
    "Realistic human bodies, hairstyles and eyes (your character, the assistant's look, agents)",
  ),
  quaternius(
    "universal-animation-library",
    "Universal Animation Library",
    "universalanimationlibrary",
    "Idle, walk, jog, sprint, jump and sitting animations",
  ),
];

/**
 * The office workers on the floors: Microsoft Rocketbox avatars and motion-capture clips (MIT,
 * "Copyright (c) 2020 Microsoft", pinned commit 0943055d). The license text ships in
 * `public/world/models/rocketbox/LICENSE` and `public/world/anim/rocketbox/LICENSE`.
 */
export const ROCKETBOX_CREDITS: readonly BuiltinCredit[] = [
  {
    id: "microsoft-rocketbox-avatars",
    name: "Rocketbox avatars (18 office looks)",
    author: "Microsoft",
    license: "MIT",
    source: "https://github.com/microsoft/Microsoft-Rocketbox",
    use: "The agents seated at their desks on the floors (medium and high quality)",
  },
  {
    id: "microsoft-rocketbox-animations",
    name: "Rocketbox motion-capture clips (seated)",
    author: "Microsoft",
    license: "MIT",
    source: "https://github.com/microsoft/Microsoft-Rocketbox",
    use: "Sitting, breathing, thinking, looking around and stretching clips of the agents",
  },
];

/** Every built-in pack of the world, for the credits page. */
export const BUILTIN_CREDITS: readonly BuiltinCredit[] = [
  ...INTERIOR_CREDITS,
  ...PLAZA_INTERIOR_CREDITS,
  ...CITY_CREDITS,
  ...CHARACTER_CREDITS,
  ...ROCKETBOX_CREDITS,
];
